/**
 * SearchBar Component
 *
 * Global search bar with command-palette style dropdown.
 * Supports keyboard navigation (Cmd/Ctrl+K to open, arrow keys, Enter, Escape).
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { PriorityIcon } from '@/components/ui/priority-icon';
import { type FlatSearchResult, useGlobalSearch } from '@/hooks/use-global-search';
import { formatDate as formatDateUtil } from '@/lib/formatting';
import { getModifierSymbol } from '@/hooks/use-keyboard-shortcuts';
import { cn } from '@/lib/utils';

const TYPE_ICONS: Record<string, string> = {
  BUG: 'bug_report',
  FEATURE: 'lightbulb',
  TASK: 'task_alt',
  IMPROVEMENT: 'trending_up',
  SUPPORT: 'support_agent',
};

// ============================================================================
// Helper Functions
// ============================================================================

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatRole(role: string): string {
  return role.replace(/_/g, ' ');
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'OPEN':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'IN_PROGRESS':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'RESOLVED':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'CLOSED':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
}

// ============================================================================
// SearchBar Component
// ============================================================================

export function SearchBar() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    query,
    setQuery,
    results,
    flatResults,
    isLoading,
    isFetching,
    hasResults,
    isQueryValid,
    resetQuery,
  } = useGlobalSearch();

  // Reset selection when results change
  // Using setTimeout to defer state update and avoid synchronous setState in effect
  useEffect(() => {
    const timeoutId = setTimeout(() => setSelectedIndex(0), 0);
    return () => clearTimeout(timeoutId);
  }, [flatResults]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      resetQuery();
      // Defer state update to avoid synchronous setState in effect
      const timeoutId = setTimeout(() => setSelectedIndex(0), 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, resetQuery]);

  // Global keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigate to result
  const navigateToResult = useCallback(
    (result: FlatSearchResult) => {
      setIsOpen(false);
      switch (result.type) {
        case 'project':
          navigate(`/projects/${result.id}`);
          break;
        case 'issue':
          navigate(`/issues/${result.id}`);
          break;
        case 'user':
          navigate(`/admin/panel/users/${result.id}`);
          break;
      }
    },
    [navigate],
  );

  // Handle keyboard navigation within dialog
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[selectedIndex]) {
            navigateToResult(flatResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [flatResults, selectedIndex, navigateToResult],
  );

  // Calculate cumulative index for each category
  const projectsEndIndex = results?.projects.length ?? 0;
  const issuesEndIndex = projectsEndIndex + (results?.issues.length ?? 0);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      {/* Trigger Button */}
      <DialogPrimitive.Trigger asChild>
        <Button
          className="relative h-9 w-full justify-start rounded-lg border border-white/30 bg-white/20 text-sm text-inherit shadow-none hover:bg-white/30 sm:w-96"
          data-testid="search-trigger"
        >
          <Icon name="search" className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline-flex">{t('search.buttonText')}</span>
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden h-5 select-none items-center gap-1 rounded border border-white/30 bg-white/20 px-1.5 font-mono text-[10px] font-medium text-inherit sm:flex">
            <span className="text-xs">{getModifierSymbol()}</span>K
          </kbd>
        </Button>
      </DialogPrimitive.Trigger>

      {/* Dialog Content */}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[20%] z-50 w-full max-w-2xl translate-x-[-50%] overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-[hsl(233,54%,16%)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onKeyDown={handleKeyDown}
          aria-describedby={undefined}
        >
          <VisuallyHidden.Root>
            <DialogPrimitive.Title>{t('actions.search')}</DialogPrimitive.Title>
          </VisuallyHidden.Root>
          {/* Search Input */}
          <div className="flex items-center border-b border-neutral-200 px-3 dark:border-neutral-700">
            <Icon name="search" className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="flex-1 border-0 bg-transparent py-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="search-input"
            />
            {(isLoading || isFetching) && (
              <Icon
                name="progress_activity"
                className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
              />
            )}
          </div>

          {/* Results */}
          <div className="max-h-[28rem] overflow-y-auto" data-testid="search-results">
            {/* Initial State */}
            {!isQueryValid && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                <Icon
                  name="search"
                  className="mx-auto mb-2 h-8 w-8 text-neutral-300 dark:text-neutral-600"
                />
                <p>{t('search.minCharacters')}</p>
              </div>
            )}

            {/* Loading State */}
            {isQueryValid && isLoading && !hasResults && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                <Icon
                  name="progress_activity"
                  className="mx-auto mb-2 h-8 w-8 animate-spin text-neutral-300 dark:text-neutral-600"
                />
                <p>{t('search.loading')}</p>
              </div>
            )}

            {/* Empty State */}
            {isQueryValid && !isLoading && !hasResults && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                <Icon
                  name="search_off"
                  className="mx-auto mb-2 h-8 w-8 text-neutral-300 dark:text-neutral-600"
                />
                <p>{t('search.noResults', { query })}</p>
              </div>
            )}

            {/* Results */}
            {hasResults && (
              <>
                {/* Projects */}
                {results?.projects && results.projects.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('search.categories.projects')}
                    </div>
                    {results.projects.map((project, index) => (
                      <button
                        key={project.id}
                        onClick={() => navigateToResult({ type: 'project', ...project })}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                          selectedIndex === index && 'bg-neutral-100 dark:bg-neutral-800',
                        )}
                        data-testid={`search-result-project-${project.id}`}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                          <Icon name="folder" className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                            <span className="font-mono text-xs text-muted-foreground">
                              {project.key}
                            </span>{' '}
                            {project.name}
                          </p>
                          {project.tenant && (
                            <p className="truncate text-xs text-muted-foreground">
                              {project.tenant.name}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Issues */}
                {results?.issues && results.issues.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('search.categories.issues')}
                    </div>
                    {results.issues.map((issue, index) => {
                      const globalIndex = projectsEndIndex + index;
                      return (
                        <button
                          key={issue.id}
                          onClick={() => {
                            const { type: issueType, ...rest } = issue;
                            navigateToResult({ type: 'issue', issueType, ...rest });
                          }}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                            selectedIndex === globalIndex && 'bg-neutral-100 dark:bg-neutral-800',
                          )}
                          data-testid={`search-result-issue-${issue.id}`}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                            <Icon
                              name={TYPE_ICONS[issue.type ?? ''] || 'bug_report'}
                              className="h-4 w-4"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              <span className="font-mono text-xs text-muted-foreground">
                                {issue.displayId}
                              </span>{' '}
                              {issue.title}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {issue.project.name}
                              {issue.assignee && (
                                <span className="ml-1.5 text-neutral-400">
                                  {' '}
                                  — {issue.assignee.name}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {issue.dueDate && (
                              <span className="text-[10px] text-muted-foreground">
                                {formatDateUtil(issue.dueDate, 'short')}
                              </span>
                            )}
                            <PriorityIcon
                              priority={issue.priority as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'}
                            />
                            <span
                              className={cn(
                                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                                getStatusColor(issue.status),
                              )}
                            >
                              {issue.status.replace('_', ' ')}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Users */}
                {results?.users && results.users.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('search.categories.users')}
                    </div>
                    {results.users.map((user, index) => {
                      const globalIndex = issuesEndIndex + index;
                      return (
                        <button
                          key={user.id}
                          onClick={() => navigateToResult({ type: 'user', ...user })}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                            selectedIndex === globalIndex && 'bg-neutral-100 dark:bg-neutral-800',
                          )}
                          data-testid={`search-result-user-${user.id}`}
                        >
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarImage
                              src={user.profilePictureUrl || undefined}
                              alt={user.name}
                            />
                            <AvatarFallback className="text-xs">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {user.name}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
                            {formatRole(user.role)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-neutral-200 px-3 py-2 text-xs text-muted-foreground dark:border-neutral-700">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-neutral-200 bg-neutral-100 px-1 dark:border-neutral-600 dark:bg-neutral-700">
                  ↑
                </kbd>
                <kbd className="rounded border border-neutral-200 bg-neutral-100 px-1 dark:border-neutral-600 dark:bg-neutral-700">
                  ↓
                </kbd>
                <span>{t('search.keyboard.navigate')}</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-neutral-200 bg-neutral-100 px-1 dark:border-neutral-600 dark:bg-neutral-700">
                  ↵
                </kbd>
                <span>{t('search.keyboard.select')}</span>
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-neutral-200 bg-neutral-100 px-1 dark:border-neutral-600 dark:bg-neutral-700">
                esc
              </kbd>
              <span>{t('search.keyboard.close')}</span>
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
